/**
 * NearestMosques — top 3 nearby mosques with distance, walk time, directions.
 *
 * Data: OpenStreetMap Overpass API (free, no key). Mirrors used in rotation
 * to avoid the 4-req/min cap on overpass-api.de.
 * Privacy: lat/lon rounded to 3 decimals (~111m precision) before query.
 * Cache: localStorage `aweni_nearest_mosques_v1` keyed by rounded coords, 24h TTL.
 * Visibility: only renders when geolocation is granted + coords resolved.
 */

import type { Widget, WidgetContext } from '../types';

const OVERPASS_MIRRORS = [
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass-api.de/api/interpreter',
];

const CACHE_KEY_PREFIX = 'aweni_nearest_mosques_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;  // 24h
const WALK_MPS = 80;  // m/min average walking pace

interface Mosque {
  id: number;
  lat: number;
  lon: number;
  name: string;
  dist: number;  // metres
}

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function roundCoord(v: number): number {
  return Math.round(v * 1000) / 1000;
}

function fmtDist(m: number): string {
  return m < 950 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}

function fmtWalk(m: number, t: (key: string) => string): string {
  const mins = Math.ceil(m / WALK_MPS);
  return (t('widget_mosques_walk_time') || '{m} min walk').replace('{m}', String(mins));
}

function cacheKey(lat: number, lon: number): string {
  return `${CACHE_KEY_PREFIX}_${lat}_${lon}`;
}

function readCache(lat: number, lon: number): Mosque[] | null {
  try {
    const raw = localStorage.getItem(cacheKey(lat, lon));
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw) as { ts: number; data: Mosque[] };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch { return null; }
}

function writeCache(lat: number, lon: number, data: Mosque[]): void {
  try {
    localStorage.setItem(cacheKey(lat, lon), JSON.stringify({ ts: Date.now(), data }));
  } catch { /* quota exceeded — skip */ }
}

async function fetchMosques(lat: number, lon: number, radiusM: number): Promise<Mosque[]> {
  const query =
    `[out:json][timeout:5];` +
    `node["amenity"="place_of_worship"]["religion"="muslim"](around:${radiusM},${lat},${lon});out;`;
  const url = (mirror: string) => `${mirror}?data=${encodeURIComponent(query)}`;
  const mirror = OVERPASS_MIRRORS[Math.floor(Math.random() * OVERPASS_MIRRORS.length)];
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 3000);
  try {
    const resp = await fetch(url(mirror), { signal: ctrl.signal });
    clearTimeout(timer);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const json = await resp.json() as { elements: Array<{ id: number; lat: number; lon: number; tags?: Record<string, string> }> };
    return json.elements
      .filter(e => e.lat && e.lon)
      .map(e => ({
        id: e.id,
        lat: e.lat,
        lon: e.lon,
        name: e.tags?.name || e.tags?.['name:en'] || '',
        dist: Math.round(haversine(lat, lon, e.lat, e.lon)),
      }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 3);
  } catch {
    clearTimeout(timer);
    return [];
  }
}

function openDirections(lat: number, lon: number, name: string): void {
  const encoded = encodeURIComponent(name || 'Mosque');
  const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&destination_place_id=${encoded}&travelmode=walking`;
  window.open(url, '_blank', 'noopener noreferrer');
}

function injectStyles(): void {
  if (document.getElementById('aweni-mosques-styles')) return;
  const s = document.createElement('style');
  s.id = 'aweni-mosques-styles';
  s.textContent = `
    .aweni-mosques {
      background: var(--card-bg, #fff);
      border: 1px solid var(--border, #E5E7EB);
      border-radius: 16px;
      padding: 14px 16px;
    }
    .dark .aweni-mosques {
      background: rgba(255,255,255,.04);
      border-color: rgba(255,255,255,.10);
    }
    .aweni-mosques-title {
      font-size: 11px; font-weight: 700; text-transform: uppercase;
      letter-spacing: .4px; color: var(--text-secondary, #6B7280); margin-bottom: 10px;
    }
    .aweni-mosques-list { display: flex; flex-direction: column; gap: 8px; }
    .aweni-mosques-row {
      display: flex; align-items: center; gap: 10px; padding: 6px 0;
      border-bottom: 1px solid var(--border, #F3F4F6);
    }
    .aweni-mosques-row:last-child { border-bottom: none; }
    .aweni-mosques-icon { font-size: 18px; flex: 0 0 auto; }
    .aweni-mosques-info { flex: 1; min-width: 0; }
    .aweni-mosques-name {
      font-size: 14px; font-weight: 600; color: var(--text-primary, #111);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .dark .aweni-mosques-name { color: #F1F5F9; }
    .aweni-mosques-meta { font-size: 12px; color: var(--text-secondary, #6B7280); }
    .aweni-mosques-dir {
      flex: 0 0 auto; font-size: 12px; font-weight: 600;
      color: var(--accent, #0A0A0A); background: none; border: none;
      cursor: pointer; padding: 4px 8px; border-radius: 8px;
      background: rgba(10,10,10,.06); transition: background 120ms;
    }
    .dark .aweni-mosques-dir { color: var(--ln-accent, #9C8348); background: rgba(255,255,255,.08); }
    .aweni-mosques-dir:hover { background: rgba(10,10,10,.12); }
    .aweni-mosques-footer {
      margin-top: 8px; font-size: 11px; color: var(--text-secondary, #9CA3AF);
      text-align: right;
    }
    .aweni-mosques-footer a { color: inherit; }
    .aweni-mosques-skeleton { display: flex; flex-direction: column; gap: 8px; }
    .aweni-mosques-skel-row {
      height: 44px; border-radius: 8px;
      background: linear-gradient(90deg, var(--border, #F3F4F6) 25%, rgba(0,0,0,.04) 50%, var(--border, #F3F4F6) 75%);
      background-size: 200% 100%;
      animation: aweni-skel-shimmer 1.4s ease infinite;
    }
    @keyframes aweni-skel-shimmer { 0%{background-position:200% 0} to{background-position:-200% 0} }
    @media (prefers-reduced-motion: reduce) { .aweni-mosques-skel-row { animation: none; } }
  `;
  document.head.appendChild(s);
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export const NearestMosques: Widget = {
  id: 'nearest-mosques',
  label: {
    en: 'Nearby mosques',
    fr: 'Mosquées proches',
    ar: 'مساجد قريبة',
    es: 'Mezquitas cercanas',
    de: 'Nahe Moscheen',
  },
  icon: '🕌',
  defaultVisible: true,
  defaultOrder: 11,
  defaultSize: 'compact',

  render(container: HTMLElement, _size, ctx: WidgetContext): void {
    injectStyles();
    const t = ctx.t;
    const loc = ctx.getLocationService();

    if (loc.lat === null || loc.lng === null) {
      container.innerHTML = '';
      return;
    }

    const rLat = roundCoord(loc.lat);
    const rLon = roundCoord(loc.lng ?? 0);
    const cached = readCache(rLat, rLon);

    // Always show skeleton immediately so the WidgetEngine slot stays alive
    // while the Overpass fetch runs in the background (~1-3s).
    container.innerHTML = `
      <div class="aweni-mosques" role="region"
           aria-label="${esc(t('widget_mosques_title') || 'Nearby mosques')}">
        <div class="aweni-mosques-title">🕌 ${esc(t('widget_mosques_title') || 'Nearby mosques')}</div>
        <div class="aweni-mosques-skeleton" aria-hidden="true">
          <div class="aweni-mosques-skel-row"></div>
          <div class="aweni-mosques-skel-row"></div>
          <div class="aweni-mosques-skel-row"></div>
        </div>
      </div>`;

    const render = (mosques: Mosque[], radius?: number) => {
      if (!mosques.length) {
        container.innerHTML = '';
        return;
      }
      const radiusNote = radius && radius > 5000
        ? ` <span style="opacity:.6">(${t('widget_mosques_within_10km') || 'within 10km'})</span>` : '';
      container.innerHTML = `
        <div class="aweni-mosques" role="region"
             aria-label="${esc(t('widget_mosques_title') || 'Nearby mosques')}">
          <div class="aweni-mosques-title">🕌 ${esc(t('widget_mosques_title') || 'Nearby mosques')}${radiusNote}</div>
          <ul class="aweni-mosques-list" role="list">
            ${mosques.map(m => {
              const name = m.name || (t('widget_mosques_unnamed') || 'Mosque');
              const dirLabel = `${t('widget_mosques_directions') || 'Directions'} — ${esc(name)}`;
              return `
                <li class="aweni-mosques-row" role="listitem"
                    aria-label="${esc(name)}, ${fmtDist(m.dist)}, ${fmtWalk(m.dist, t)}">
                  <span class="aweni-mosques-icon" aria-hidden="true">🕌</span>
                  <div class="aweni-mosques-info">
                    <div class="aweni-mosques-name">${esc(name)}</div>
                    <div class="aweni-mosques-meta">${fmtDist(m.dist)} · ${fmtWalk(m.dist, t)}</div>
                  </div>
                  <button class="aweni-mosques-dir" type="button"
                          aria-label="${esc(dirLabel)}"
                          data-lat="${m.lat}" data-lon="${m.lon}" data-name="${esc(name)}">
                    ${esc(t('widget_mosques_directions') || 'Directions')}
                  </button>
                </li>`;
            }).join('')}
          </ul>
          <div class="aweni-mosques-footer">
            <a href="https://www.google.com/maps/search/mosque/@${rLat},${rLon},14z"
               target="_blank" rel="noopener noreferrer">
              ${esc(t('widget_mosques_see_all') || 'See all on map')} ↗
            </a>
          </div>
        </div>
      `;
      container.querySelectorAll<HTMLButtonElement>('.aweni-mosques-dir').forEach(btn => {
        btn.addEventListener('click', () => {
          openDirections(
            parseFloat(btn.dataset.lat || '0'),
            parseFloat(btn.dataset.lon || '0'),
            btn.dataset.name || '',
          );
        });
      });
    };

    // Render from cache immediately
    if (cached) render(cached);

    // Background fetch
    const doFetch = async () => {
      let mosques = await fetchMosques(rLat, rLon, 5000);
      let radius = 5000;
      if (mosques.length < 3) {
        mosques = await fetchMosques(rLat, rLon, 10000);
        radius = 10000;
      }
      if (mosques.length) {
        writeCache(rLat, rLon, mosques);
        render(mosques, radius);
      } else if (!cached) {
        container.innerHTML = '';  // hide if truly no results
      }
    };

    if (!cached) {
      doFetch();
    } else {
      // Stale-while-revalidate: re-fetch in background without blocking paint
      requestIdleCallback?.(() => doFetch()) ?? setTimeout(doFetch, 2000);
    }
  },
};
